<template>
  <m-item-wrapper>
    <div class="account-menu">
      <m-dropdown
        class="_menu"
        title=" "
      >
        <div
          :class="['_trigger']"
        >
          <div class="_user">
            <m-avatar
              class="_avatar"
              :src="companyImage"
              :text="account.companyName"
              :amount-chars="1"
              :size="20"
            />
            <div class="_name">
              {{ account.companyName }}
            </div>
            <m-icon
              type="down"
              :color="$colors.grey.lighten1"
              size="10"
            />
          </div>
          <div
            v-if="showClose && !$store.state.breakpoint.smAndDown"
            class="_close"
          >
            <m-btn
              small
              :icon="desktopSidebarHidden ? 'double-right' : 'double-left'"
              hide-border
              fab
              light
              @click.stop="$emit('toggle-sidebar')"
            />
          </div>
        </div>
        <template #overlay>
          <m-card
            no-padding
            class="_overlay"
            list
          >
            <div class="_header">
              <div class="_avatar _header-avatar">
                <m-avatar
                  :src="companyImage"
                  :text="account.companyName"
                  :amount-chars="1"
                  :size="36"
                />
              </div>
              <div class="_meta">
                <div class="_name">
                  {{ account.companyName }}
                </div>
                <div class="_sub">
                  {{ planName }} · {{ $t('spaceTopBarMenu.members', { count: activeMembers }) }}
                </div>
              </div>
            </div>
            <m-divider
              none
              :style="{ marginBottom: '.4rem' }"
            />
            <m-card-item
              icon="settings"
              @click="$router.push({ name: routeName.profileSettings })"
            >
              {{ $t('navigation.settings') }}
            </m-card-item>
            <m-card-item
              v-if="canInviteUsers"
              icon="user-group-add"
              :disabled="isExpired"
              @click="EventBus.$emit('show-invite-users-dialog')"
            >
              {{ $t('mainNavigation.invitePeople') }}
            </m-card-item>
            <m-card-item
              icon="logout"
              @click="EventBus.$emit('logout')"
            >
              {{ $t('navigation.logout') }}
            </m-card-item>
          </m-card>
        </template>
      </m-dropdown>
    </div>
  </m-item-wrapper>
</template>

<script>
import useAccess from '@/composables/access/access';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useExpiredAccount from '@/composables/expired-account/expired-account';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useUsers from '@/composables/user/users';
import { EventBus } from '@/lib/event-bus';
import { SIDEBAR_SERVICE } from '@/lib/constants';
import { accessGroupFlag, routeName } from 'shared/constants.json';
import { camelCase } from 'lodash-es';
import { inject } from 'vue';

export default {
  name: 'AccountMenu',
  props: {
    collapsed: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { loggedInUser } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { accountSettings } = useAccountSettings();
    const { desktopSidebarHidden } = inject(SIDEBAR_SERVICE);
    const { userHasRight } = useAccess();
    const { users, fetchUsers } = useUsers();
    const { isExpired } = useExpiredAccount();
    return {
      user: loggedInUser,
      desktopSidebarHidden,
      account: loggedInUserAccount,
      accountSettings,
      userHasRight,
      users,
      fetchUsers,
      isExpired,
    };
  },
  data() {
    return { EventBus, routeName };
  },
  emits: ['toggle-sidebar'],
  computed: {
    companyImage() {
      return this.account.companyImage?.getURL || '';
    },
    canInviteUsers() {
      return this.userHasRight([accessGroupFlag.userWriteAccess]);
    },
    activeMembers() {
      return this.users.length;
    },
    planName() {
      const planId = this.accountSettings?.planId;
      return planId ? this.$t(`planHint.${camelCase(planId)}`) : '';
    },
  },
  created() {
    if (this.users.length === 0) {
      this.fetchUsers();
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .account-menu {
    border-radius: $btn-border-radius;

    &:hover {
      background-color: $sidebar-hover-color;
    }
  }

  ._menu {
    width: 100%;
  }

  ._trigger {
    display: flex;
    align-items: center;
    cursor: pointer;

    ._user {
      display: flex;
      align-items: center;
      padding: .6rem 1.2rem .6rem .8rem;

      ._avatar {
        margin-right: .65rem;
      }

      ._name {
        max-width: 12rem;
        margin-right: .4rem;
        overflow: hidden;
        font-weight: $font-weight-medium;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    ._close {
      padding-right: .8rem;
      margin-left: auto;
    }
  }

  ._overlay {
    min-width: 25rem;
  }

  ._header {
    display: flex;
    align-items: center;
    padding: .8rem 1.2rem;

    ._header-avatar {
      margin-right: .8rem;
    }

    ._meta {
      display: flex;
      flex-direction: column;

      ._name {
        font-weight: $font-weight-bold;
        max-width: 16rem;
        margin-right: .4rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ._sub {
        color: $font-color-secondary;
      }
    }
  }
</style>
